import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { Ref } from "vue";
import type { ApplyAnswer, PlanAnswer, ProposalDoor } from "@/shared/contracts/proposal";
import type { VisualIdentity } from "@/shared/contracts/visual-identity";
import { HttpError, request } from "@/shared/http/client";

/**
 * The first day, as DERIVED state.
 *
 * There is no onboarding session table and no state machine behind this. The
 * server reads the `mission_run` rows and the existence of two files and answers
 * with a phase — which is why closing the tab and coming back tomorrow is not a
 * feature anybody built: nothing here is ephemeral, so there was nothing to
 * persist. The screen therefore never stores a phase of its own; it asks.
 *
 * Two calls START something and neither one waits for it. `POST /research` and
 * `POST /design` launch an ASYNCHRONOUS mission and answer with its id
 * immediately, so the browser is never holding a socket open for the ninety
 * seconds a mission takes — the live view is `useMission(id)` polling, exactly
 * as the missions screen already does.
 */
export type OnboardingPhase =
	| "creating"
	| "creation_ready"
	| "idle"
	| "researching"
	| "dossier_ready"
	| "designing"
	| "proposal_ready"
	/** Há rascunho, mas o último plano ficou bloqueado: nada a aplicar; "Continuar" retoma o Ben. */
	| "proposal_blocked"
	/** A proposta já foi aplicada (e arquivada) — o sistema está de pé (QA-019). */
	| "applied"
	/** A pessoa pulou o primeiro dia: nada se gera, nada se propõe (`PUT /skip`). */
	| "skipped";

export interface OnboardingStartInput {
	readonly newBusiness: boolean;
	readonly businessName: string;
}

export interface OnboardingState {
	readonly start?: OnboardingStartInput | null;
	readonly phase: OnboardingPhase;
	readonly identityOnly?: boolean;
	readonly newBusiness?: boolean;
	readonly creationPath?: string;
	/** The run to watch, when something is running or just finished. */
	readonly missionId: string | null;
	/**
	 * TODO o primeiro dia, da corrida mais antiga para a que corre agora.
	 *
	 * A tela da espera conta o percurso a partir dos PASSOS de uma missão, e cada
	 * fase é uma missão nova — então a virada da pesquisa para o perfil apagava de
	 * uma vez tudo o que a pessoa tinha visto acumular durante cinco minutos, e o
	 * primeiro dia recomeçava do zero à frente de quem estava a olhar. Com a lista
	 * inteira o percurso é contínuo: o que já aconteceu continua escrito enquanto
	 * o resto acontece.
	 *
	 * `missionId` continua a ser a corrida VIVA — o estado, o erro e o relógio são
	 * dela —, e é a última desta lista.
	 */
	readonly missionIds: readonly string[];
	readonly dossierPath: string;
	readonly draftPath: string;
	readonly hasDossier: boolean;
	readonly hasDraft: boolean;
	/**
	 * Whether a mission can be launched at all.
	 *
	 * `false` with no plan: without a `credit_balance` row the invocation gate
	 * refuses every turn, so a button that launched anyway would spend a person's
	 * attention on a run that dies at step zero.
	 */
	readonly canRun: boolean;
	readonly blockedReason: string | null;
	/** Pendências orientativas do primeiro dia; não bloqueiam a criação do site. */
	readonly siteMissing: readonly SiteBlocker[];
	/** Alguma versão de site já rascunhada — o passo da home fecha com isto. */
	readonly hasSite: boolean;
	/** O funil onde as conversas pousam, marcado pela proposta aplicada (ausente num servidor antigo). */
	readonly conversationFunnel?: string | null;
	/** Alguém já assumiu uma conversa — fica depois do "Devolver" (ausente num servidor antigo). */
	readonly threadAssumed?: boolean;
	/** O site da trilha a ser construído por uma missão (`useStartSite`); null = nunca pedido. */
	readonly siteBuild?: { readonly missionId: string; readonly status: string } | null;
	/** O sistema do código de parceiro que o cadastro não instalou (ausente num servidor antigo). */
	readonly blueprintPending?: { readonly name: string } | null;
	/** O catálogo que a pesquisa cadastra depois da identidade; null = nunca lançado. */
	readonly catalogBuild?: CatalogBuild | null;
}

export interface CatalogBuild {
	readonly missionId: string;
	readonly status: string;
	/** Por tabela, quando a missão terminou; `count` é o número de linhas de agora. */
	readonly tables: readonly {
		readonly entity: string;
		readonly name: string;
		readonly count: number;
		/** Registros com foto, contados no servidor; null = sem campo de foto declarado. */
		readonly photos?: number | null;
		readonly partial: boolean;
		readonly sources: readonly string[];
	}[];
	readonly notes: string;
}

/** O catálogo ainda corre: a tela mostra a linha de espera e o estado continua a ser lido. */
export function catalogBuilding(state: OnboardingState | null | undefined): boolean {
	return ["queued", "running", "waiting_human"].includes(state?.catalogBuild?.status ?? "");
}

/** A construção do site ainda corre: a trilha mostra "sendo construído" e o estado continua a ser lido. */
export function siteBuilding(state: OnboardingState | null | undefined): boolean {
	return ["queued", "running", "waiting_human"].includes(state?.siteBuild?.status ?? "");
}

export type SiteBlocker = "no_structure" | "no_published_chat" | "open_gaps" | "pending_curation";

export interface CreationIdentity extends Partial<VisualIdentity> {
	readonly name?: string;
	readonly logoFileId?: string;
	readonly notes?: string;
}

export interface ResearchInput {
	readonly newBusiness?: boolean;
	readonly fileIds?: string[];
	readonly identity?: CreationIdentity;
	readonly businessName: string;
	readonly website?: string;
	readonly instagram?: string;
	readonly city?: string;
	readonly description?: string;
	/** "É a de Curitiba", "o site é outro" — a second pass over the same name. */
	readonly correction?: string;
}

export interface DesignInput {
	/** The dossier, replaced by what the person typed, when research found nothing. */
	readonly ownDescription?: string;
}

export const onboardingKeys = { state: ["onboarding", "state"] as const };

/** The phases where something still moves on its own. */
const WORKING: ReadonlySet<OnboardingPhase> = new Set(["creating", "researching", "designing"]);

/**
 * The phase, and the polling that stops.
 *
 * Four seconds while a mission runs or a transient state read needs recovery.
 * Stops after a successful settled read: a tab left
 * open on a finished onboarding must not be a request every four seconds for the
 * rest of the day. Same leash and same reason as `useMission`.
 *
 * ── POR QUE AS DUAS EXCEÇÕES AO PADRÃO GLOBAL ─────────────────────────────
 *
 * `refetchInterval` só dispara com a janela em foco, e o cliente global desliga
 * `refetchOnWindowFocus`. Numa tela normal isso é o certo. Aqui as duas coisas
 * juntas produziram o único bug que esta tela não pode ter: quem sai da aba para
 * fazer outra coisa enquanto a missão roda para de receber qualquer atualização,
 * e ao voltar não recebe nenhuma de recuperação — a pesquisa terminou às 15:38 e
 * a tela só contou às 15:42. A espera é justamente o momento em que a pessoa vai
 * olhar outra coisa; é a aba em segundo plano que precisa continuar perguntando.
 */
export function useSaveOnboardingStart() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: OnboardingStartInput) =>
			request("/v3/onboarding/start", { method: "PUT", body: input }),
		onSuccess: () => client.invalidateQueries({ queryKey: onboardingKeys.state }),
	});
}

/** `streaming`: o fio ao vivo da missão está aberto — o evento chama, o relógio cala. */
export function useOnboardingState(streaming?: Ref<boolean>) {
	return useQuery({
		queryKey: onboardingKeys.state,
		queryFn: () => request<OnboardingState>("/v3/onboarding/state"),
		refetchInterval: (query) =>
			(query.state.data && WORKING.has(query.state.data.phase) && !streaming?.value) ||
			siteBuilding(query.state.data) ||
			catalogBuilding(query.state.data) ||
			(query.state.error instanceof HttpError &&
				["timeout", "network", "server"].includes(query.state.error.kind))
				? 4_000
				: false,
		// A missão termina no servidor, não na aba. O leash acima continua fechando
		// sozinho quando a fase assenta, então isto não é um poll eterno.
		refetchIntervalInBackground: true,
		refetchOnWindowFocus: true,
	});
}

/**
 * Launching one of the two missions.
 *
 * Only the phase is invalidated, and that is the whole point: the answer arrives
 * in milliseconds with an id, the phase turns to `researching`, and the poll
 * above takes over. Nothing here waits for the mission.
 */
function useLaunch<Input>(path: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: Input) =>
			request<{ missionId: string }>(path, { method: "POST", body: input }),
		// A lost launch response does not mean the server rejected the mission.
		// Reconcile its state without replaying the POST.
		onSettled: () =>
			Promise.all([
				client.invalidateQueries({ queryKey: onboardingKeys.state }),
				client.invalidateQueries({
					queryKey: ["files", "content", "/onboarding/creation-input.json"],
				}),
				client.invalidateQueries({ queryKey: ["files", "content", "/onboarding/creation.json"] }),
			]),
	});
}

export function useResumeOnboarding() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: () => request<{ missionId: string }>("/v3/onboarding/resume", { method: "POST" }),
		// A lost launch response does not mean the server rejected the mission.
		// Reconcile its state without replaying the POST.
		onSettled: () =>
			Promise.all([
				client.invalidateQueries({ queryKey: onboardingKeys.state }),
				client.invalidateQueries({ queryKey: ["missions"] }),
			]),
	});
}

/** Pular o primeiro dia (`true`) ou voltar a fazê-lo (`false`); a resposta é o estado novo. */
export function useSkipOnboarding() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (skipped: boolean) =>
			request<OnboardingState>("/v3/onboarding/skip", { method: "PUT", body: { skipped } }),
		onSuccess: (state) => client.setQueryData(onboardingKeys.state, state),
		onSettled: () => client.invalidateQueries({ queryKey: onboardingKeys.state }),
	});
}

/** "Tentar instalar de novo" o sistema do parceiro; a resposta é o estado novo. */
export function useRetryBlueprint() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: () =>
			request<OnboardingState>("/v3/onboarding/blueprint/retry", {
				method: "POST",
				timeoutMs: 180_000,
			}),
		onSuccess: (state) => client.setQueryData(onboardingKeys.state, state),
		onSettled: () => client.invalidateQueries({ queryKey: onboardingKeys.state }),
	});
}

export function useStartResearch() {
	return useLaunch<ResearchInput>("/v3/onboarding/research");
}

export function useStartDesign() {
	return useLaunch<DesignInput>("/v3/onboarding/design");
}

/** "Criar meu site" na trilha: o servidor lança a missão e a pessoa segue de etapa. */
export function useStartSite() {
	return useLaunch<void>("/v3/onboarding/site");
}

/**
 * A PORTA DA PROPOSTA DO PRIMEIRO DIA (WP-D1).
 *
 * O mesmo planeador de "Seu sistema" — o plano em passos, o apply idempotente,
 * o fragmento com desfazer —, entrando por `/v3/onboarding/proposal/*` porque
 * é a ROTA que assina: o fragmento fica com autoria `onboarding`, e o
 * histórico diz "o primeiro dia montou isto" em vez de "você pediu «proposta»".
 * O cartão da proposta é o mesmo da vitrine e da proposta do Ben; só a porta
 * muda.
 *
 * As lacunas da pesquisa (as perguntas de /help) nascem no servidor, dentro do
 * apply — o POST à parte que esta tela chamava depois, em melhor esforço,
 * deixou de existir.
 */
export const onboardingProposalDoor: ProposalDoor = {
	plan: (input) =>
		request<PlanAnswer>("/v3/onboarding/proposal/plan", {
			method: "POST",
			body: { patch: input.patch, request: input.request },
			// Planear compila e ensaia as funções da proposta no sandbox.
			timeoutMs: 120_000,
		}),
	apply: (input) =>
		request<ApplyAnswer>("/v3/onboarding/proposal/apply", {
			method: "POST",
			body: { patch: input.patch, request: input.request },
			timeoutMs: 180_000,
		}),
};
